import type { CorSelo } from '../../componentes/base';

export type Contrato = 'clt' | 'pj' | 'estagio' | 'temporario';
export type Estado = 'ativo' | 'afastado' | 'desligado';
export type Situacao = 'sem_validade' | 'vencido' | 'vencendo' | 'valido';
export type Escala = '5x2' | '6x1' | '12x36';

export const CONTRATOS: Contrato[] = ['clt', 'pj', 'estagio', 'temporario'];
export const ESTADOS: Estado[] = ['ativo', 'afastado', 'desligado'];
export const ESCALAS: Escala[] = ['5x2', '6x1', '12x36'];
export const DIAS = ['seg', 'ter', 'qua', 'qui', 'sex', 'sab', 'dom'] as const;
export type Dia = (typeof DIAS)[number];

export const COR_CONTRATO: Record<Contrato, CorSelo> = { clt: 'azul', pj: 'roxo', estagio: 'ciano', temporario: 'laranja' };
export const COR_ESTADO: Record<Estado, CorSelo> = { ativo: 'verde', afastado: 'laranja', desligado: 'neutro' };
export const COR_SITUACAO: Record<Situacao, CorSelo> = { sem_validade: 'neutro', valido: 'verde', vencendo: 'laranja', vencido: 'perigo' };

export interface Funcionario {
  id: string;
  nome: string;
  /** Lote de 19/10: nulo enquanto a ficha está "a completar". */
  cpf: string | null;
  email: string | null;
  telefone: string | null;
  nascimento: string | null;
  contrato_tipo: Contrato;
  /** Lote de 19/10: nula enquanto a ficha está "a completar". */
  admissao: string | null;
  fim_contrato: string | null;
  desligamento: string | null;
  salario_centavos: number | null;
  moeda: string;
  departamento_id: string | null;
  departamento_nome: string | null;
  funcao_id: string | null;
  funcao_nome: string | null;
  gestor_id: string | null;
  gestor_nome: string | null;
  calendario_id: string | null;
  calendario_nome: string | null;
  estado: Estado;
  estado_desde: string | null;
  estado_motivo: string | null;
  usuario_id: string | null;
  /** Val7 (29/10): as formas de acesso ativas do usuário ligado (null sem usuário). */
  formas_acesso?: ('email' | 'usuario')[] | null;
  convite_pendente: boolean;
  onboarding_total: number;
  onboarding_concluidos: number;
  extras: Record<string, unknown>;
  created_at: string;
  /** Fase 2 (W16): visto só por um vínculo numa empresa sua (o cadastro é de outra empresa): só nome, CPF e esse vínculo. */
  so_por_vinculo?: boolean;
  /** Fase 2 (W23, K10): parceiro do Cadastros ligado ao funcionário (decisão 14). */
  parceiro_id?: string | null;
  /** Lote de 19/10: o que falta (CPF, admissão) para a Folha e o restante; vazio com a ficha completa. */
  /** Val1 (28/10): o que falta para o cadastro ficar completo (selo "Dados a complementar"). */
  a_completar?: ('cpf' | 'admissao' | 'funcao' | 'departamento' | 'onboarding')[];
  /** Val1 (28/10): situação do acesso ao sistema. */
  acesso?: SituacaoAcesso;
}

/** Fase 2 (W16): vínculo do funcionário com uma empresa do grupo. */
export interface Vinculo {
  id: string;
  funcionario_id: string;
  empresa_id: string;
  empresa_nome: string;
  principal: boolean;
  contrato_tipo: Contrato;
  admissao: string;
  fim_contrato: string | null;
  desligamento: string | null;
  salario_centavos: number | null;
  moeda: string;
  departamento_id: string | null;
  departamento_nome: string | null;
  funcao_id: string | null;
  funcao_nome: string | null;
  estado: Estado;
}

/** Fase 2 (W16): resposta de `GET /pessoas/funcionarios-duplicidade`. */
export interface Duplicidade {
  cpf: { id: string; nome: string } | null;
  cpf_em_outra_empresa: boolean;
  cpf_em_outro_workspace: boolean;
  possiveis: { id: string; nome: string }[];
  vinculo_aqui: boolean;
}

export interface DocumentoItem {
  id: string;
  funcionario_id: string;
  funcionario_nome: string;
  tipo: string;
  numero: string | null;
  validade: string | null;
  anexo_id: string | null;
  situacao: Situacao;
  dias_para_vencer: number | null;
}

export interface Ficha extends Funcionario {
  documentos: DocumentoItem[];
  habilidades: { habilidade_id: string; nome: string; categoria: string | null; nivel: number }[];
  historico: { id: string; funcao_id: string | null; funcao_nome: string | null; salario_centavos: number | null; moeda: string; inicio: string; fim: string | null; motivo: string | null }[];
  onboarding: { item_id: string; titulo: string; descricao: string | null; ordem: number; prazo: string | null; concluido_em: string | null; concluido_por_nome: string | null }[];
  experiencia: { fim45: string; fim90: string } | null;
  /** Fase 2 (W23, K10): nome e documento do parceiro ligado; só vem para quem vê o Cadastros. */
  parceiro?: { id: string; nome: string; documento: string | null; arquivado: boolean } | null;
}

export interface Departamento {
  id: string;
  nome: string;
  parent_id: string | null;
  parent_nome: string | null;
  /** O primeiro gestor (compatibilidade). */
  gestor_funcionario_id: string | null;
  gestor_nome: string | null;
  /** Lote de 19/10: gestores em ordem. */
  gestores?: GestorDepartamento[];
  funcionarios: number;
  funcoes: number;
}

export interface GestorDepartamento {
  id: string;
  nome: string;
}

export interface Funcao {
  id: string;
  nome: string;
  departamento_id: string | null;
  departamento_nome: string | null;
  papel_id: string | null;
  papel_nome: string | null;
  descricao: string | null;
  funcionarios: number;
  itens_onboarding: number;
}

export interface ItemModelo {
  id: string;
  titulo: string;
  descricao: string | null;
  ordem: number;
  prazo_dias: number | null;
}

/** Registro de ponto (lote 1): horário de um dia (HH:MM; intervalo opcional). */
export interface HorarioDia {
  entrada: string;
  intervalo_inicio: string | null;
  intervalo_fim: string | null;
  saida: string;
}

export interface Calendario {
  id: string;
  nome: string;
  escala: Escala;
  horas: Record<Dia, number>;
  horarios?: Partial<Record<Dia, HorarioDia | null>>;
  horas_semana: number;
  funcionarios: number;
}

export interface Habilidade {
  id: string;
  nome: string;
  categoria: string | null;
  funcionarios: number;
}

export interface Matriz {
  habilidades: { id: string; nome: string; categoria: string | null }[];
  funcionarios: { id: string; nome: string; funcao_nome: string | null; departamento_nome: string | null; niveis: Record<string, number> }[];
}

export interface Contratos {
  por_tipo: { contrato_tipo: Contrato; total: number; folha_centavos: number | null }[];
  admissoes: { id: string; nome: string; contrato_tipo: Contrato; admissao: string; funcao_nome: string | null; dias_de_casa: number }[];
  experiencia: { id: string; nome: string; admissao: string; funcao_nome: string | null; fim_45: string; fim_90: string; proximo_marco: 45 | 90; dias_para_marco: number }[];
  fim_previsto: { id: string; nome: string; contrato_tipo: Contrato; fim_contrato: string; funcao_nome: string | null; dias_para_fim: number }[];
}

/** CPF formatado para exibição. */
/** CPF e telefone pelo formatador central (que respeita o modo discreto). */
export { documentoFormatado as cpfFormatado, telefoneFormatado } from '../../nucleo/formato';

/** Remove vazios para o corpo da requisição: '' vira null. */
export function limpar(v: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(v).map(([k, x]) => [k, x === '' ? null : x]));
}

/**
 * Papéis de fábrica (dono, administrador, gestor, operador, leitura) são traduzidos pelo código;
 * papel personalizado mostra o nome gravado. Quando a API só entrega o nome, o nome de fábrica em português
 * faz o papel de código.
 */
export function nomeDoPapel(t: (chave: string, opcoes?: { defaultValue?: string }) => string, p: { nome: string; codigo?: string; de_fabrica?: boolean }): string {
  if (p.de_fabrica === false) return p.nome;
  const codigo = p.codigo ?? p.nome.trim().toLowerCase();
  return t(`papelFabrica.${codigo}`, { defaultValue: p.nome });
}

/** Val1 (28/10): situação do acesso ao sistema (a mesma ordem do filtro da lista). */
export const SITUACOES_ACESSO = ['com_acesso', 'convite_enviado', 'convite_expirado', 'sem_usuario', 'sem_email'] as const;
export type SituacaoAcesso = (typeof SITUACOES_ACESSO)[number];
